import { Button, Group, LoadingOverlay, Stack, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useState } from 'react';
import { z } from 'zod';

import { useAdminImportCustomsError } from '@admin/hooks';
import { UploadFile } from '@components';

interface ImportCustomsErrorModalProps {
  onClose: () => void;
}

const importCustomsErrorSchema = z.object({
  file: z.instanceof(File, { message: 'Please select a file.' }),
});

type ImportCustomsErrorValues = z.infer<typeof importCustomsErrorSchema>;

export const ImportCustomsErrorModal: React.FC<ImportCustomsErrorModalProps> = ({ onClose }) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const importMutation = useAdminImportCustomsError();

  const form = useForm<ImportCustomsErrorValues>({
    initialValues: { file: null },
    validate: (values) => {
      try {
        importCustomsErrorSchema.parse(values);
      } catch (e: any) {
        return { file: e.errors?.[0]?.message || 'Please select a file.' };
      }
    },
    validateInputOnBlur: true,
  });

  const handleSubmit = async (values: ImportCustomsErrorValues) => {
    setErrorMessage(null);
    try {
      await importMutation.mutateAsync(values.file);
      onClose();
    } catch (error) {
      setErrorMessage((error as any).response?.data?.message ?? 'Import failed');
    }
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="sm" mb="xl">
        <UploadFile {...form.getInputProps('file')} isMulti={false} type="json" p="xs" />
        {form.errors.file && (
          <Text c="red" fw="bold">
            {form.errors.file}
          </Text>
        )}
        {errorMessage && (
          <Text c="red" fw="bold">
            {errorMessage}
          </Text>
        )}
      </Stack>
      <Group justify="center">
        <Button type="button" variant="transparent" onClick={onClose} disabled={importMutation.isPending} size="lg">
          Cancel
        </Button>
        <Button type="submit" loading={importMutation.isPending} disabled={!form.isValid()} variant="filled" size="lg">
          Import
        </Button>
      </Group>
      <LoadingOverlay visible={importMutation.isPending} overlayProps={{ radius: 'sm', blur: 2 }} />
    </form>
  );
};
